import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useCompanyValues } from '@/providers';
import { audiencerateApi } from '@/services/audiencerate';
import {
  GetMarketIntelligenceReportResponse,
  MarketIntelligenceReportErrorKind,
  useGetMarketIntelligenceReportsQuery,
  useRetryMarketIntelligenceReportMutation,
  useStartMarketIntelligenceAnalysisMutation,
} from '@/services/endpoints/marketIntelligence';
import { MarketIntelligenceReportData, MarketIntelligenceReportFailure, MarketIntelligenceSetupData, MarketIntelligenceTab } from '../types';
import { extractMarketIntelligenceErrorMessage, hasHttpUrlScheme } from '../utils';

const MAX_REPORT_RETRY_COUNT = 3;

const RESEARCH_SCOPE_TO_API_SCOPE = {
  local: 'LOCAL',
  national: 'NATIONAL',
  global: 'GLOBAL',
} as const;

const API_SCOPE_TO_RESEARCH_SCOPE = {
  LOCAL: 'local',
  NATIONAL: 'national',
  GLOBAL: 'global',
} as const;

const ensureProtocol = (value: string) => {
  const trimmedValue = value.trim();

  if (hasHttpUrlScheme(trimmedValue)) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
};

const extractDomain = (value: string) => {
  try {
    return new URL(ensureProtocol(value)).hostname.replace(/^www\./, '');
  } catch {
    return value
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }
};

const formatGeneratedAt = (language: string, date: Date) =>
  new Intl.DateTimeFormat(language || 'en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

const buildCompetitorDescription = (competitor: NonNullable<GetMarketIntelligenceReportResponse['report']>['competitors'][number]) =>
  [
    competitor.tagline,
    competitor.knownRevenue ? `Revenue: ${competitor.knownRevenue}` : '',
    competitor.employeesRange ? `Employees: ${competitor.employeesRange}` : '',
    competitor.geographicPresence ? `Presence: ${competitor.geographicPresence}` : '',
  ]
    .filter(Boolean)
    .join(' | ');

type ReportErrorModalState = {
  reportId: string | null;
  retryCount: number;
  errorKind: MarketIntelligenceReportErrorKind | null;
  errorMessage: string;
};

export const useMarketIntelligenceReportFlow = () => {
  const { i18n, t } = useTranslation('marketIntelligence');
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const dispatch = useDispatch();
  const invalidateCredits = useCallback(() => {
    dispatch(audiencerateApi.util.invalidateTags(['Credits']));
  }, [dispatch]);
  const [startAnalysis] = useStartMarketIntelligenceAnalysisMutation();
  const [retryReport, { isLoading: isRetryingReport }] = useRetryMarketIntelligenceReportMutation();
  const { data: reportsData, isFetching: isFetchingReportsData } = useGetMarketIntelligenceReportsQuery({
    workspaceId: workspace.id,
    limit: 1,
    offset: 0,
  });
  const latestReport = reportsData?.items[0] ?? null;
  const shouldLoadLatestCompletedReport = Boolean(latestReport && latestReport.status !== 'DONE');
  const latestReportGenerationFailed = latestReport?.status === 'ERROR';
  const { data: completedReportsData, isFetching: isFetchingCompletedReportsData } = useGetMarketIntelligenceReportsQuery(
    {
      workspaceId: workspace.id,
      limit: 1,
      offset: 0,
      status: 'DONE',
    },
    { skip: !shouldLoadLatestCompletedReport }
  );
  const [activeTab, setActiveTab] = useState<MarketIntelligenceTab>('setup');
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastRequest, setLastRequest] = useState<MarketIntelligenceSetupData | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [report, setReport] = useState<MarketIntelligenceReportData | null>(null);
  const [reportErrorModal, setReportErrorModal] = useState<ReportErrorModalState | null>(null);
  const currentReportIdRef = useRef<string | null>(null);

  const buildReportData = useCallback(
    (response: GetMarketIntelligenceReportResponse): MarketIntelligenceReportData | null => {
      if (!response.report) {
        return null;
      }

      return {
        analyzedDomain: lastRequest ? extractDomain(lastRequest.yourWebsite) : '',
        industry: response.sector || lastRequest?.industry || '',
        scopeLabel: response.effectiveScope
          ? t(`setup.researchScope.${API_SCOPE_TO_RESEARCH_SCOPE[response.effectiveScope]}.label`)
          : lastRequest
            ? t(`setup.researchScope.${lastRequest.researchScope}.label`)
            : '',
        generatedAt: formatGeneratedAt(i18n.language || 'en', new Date(response.createdOn)),
        competitors: response.report.competitors.map((competitor, index) => ({
          id: `competitor-${index + 1}`,
          name: competitor.name,
          description: buildCompetitorDescription(competitor),
        })),
        strengths: response.report.strengths.map((strength, index) => ({
          id: `strength-${index + 1}`,
          title: strength.title,
          description: strength.description,
        })),
        competitorsEdge: response.report.competitorsEdge.map((item, index) => ({
          id: `competitor-edge-${index + 1}`,
          title: item.title,
          description: item.description,
        })),
        actions: [
          {
            id: 'targeting-opportunity',
            eyebrow: t('mockReport.actions.targetingOpportunity.eyebrow'),
            title: t('mockReport.actions.targetingOpportunity.title'),
            description: response.report.targetingOpportunity,
            ctaLabel: t('mockReport.actions.targetingOpportunity.ctaLabel'),
            ctaType: 'audience',
          },
          {
            id: 'defense-strategy',
            eyebrow: t('mockReport.actions.defenseStrategy.eyebrow'),
            title: t('mockReport.actions.defenseStrategy.title'),
            description: response.report.defenseStrategy,
            ctaLabel: t('mockReport.actions.defenseStrategy.ctaLabel'),
            ctaType: 'campaign',
          },
        ],
        suggestedAudiences: (response.report.actions ?? [])
          .filter((action) => action.type === 'BUILD_AUDIENCE')
          .map((action, index) => ({
            id: `suggested-audience-${index + 1}`,
            title: action.title,
            description: action.description,
            evidence: action.evidence,
            suggestedDatasourceIds: action.suggestedDatasourceIds,
            suggestedMatchingTrait: action.suggestedMatchingTrait,
            rawAction: action,
          })),
      };
    },
    [i18n.language, lastRequest, t]
  );

  const isReportHistoryLoading =
    (!reportsData && isFetchingReportsData) || (shouldLoadLatestCompletedReport && !completedReportsData && isFetchingCompletedReportsData);

  const latestCompletedReport = useMemo(() => {
    if (latestReport?.status === 'DONE' && latestReport.report) {
      return latestReport;
    }

    return completedReportsData?.items[0]?.report ? completedReportsData.items[0] : null;
  }, [completedReportsData?.items, latestReport]);

  useEffect(() => {
    if (report || reportId || isGenerating || !latestCompletedReport) {
      return;
    }

    const latestReport = buildReportData(latestCompletedReport);

    if (latestReport) {
      setReport(latestReport);
    }
  }, [buildReportData, isGenerating, latestCompletedReport, report, reportId]);

  const handleReportReady = useCallback(
    (response: GetMarketIntelligenceReportResponse) => {
      if (response.reportId !== currentReportIdRef.current) {
        return;
      }

      const nextReport = buildReportData(response);

      if (!nextReport) {
        return;
      }

      setReport(nextReport);
      setReportId(response.reportId);
      setReportErrorModal(null);
      setActiveTab('report');
      invalidateCredits();
    },
    [buildReportData, invalidateCredits]
  );

  const generateReport = useCallback(
    async (payload: MarketIntelligenceSetupData) => {
      setLastRequest(payload);
      setIsGenerating(true);
      setReport(null);
      setReportId(null);
      setReportErrorModal(null);
      currentReportIdRef.current = null;

      try {
        const competitorUrls = payload.competitorWebsites.filter((website) => website.trim().length > 0);
        const response = await startAnalysis({
          workspaceId: workspace.id,
          body: {
            clientUrl: payload.yourWebsite.trim(),
            ...(competitorUrls.length > 0 ? { competitorUrls } : {}),
            ...(payload.industry.trim() ? { sector: payload.industry.trim() } : {}),
            scope: RESEARCH_SCOPE_TO_API_SCOPE[payload.researchScope],
            language: currentLanguage,
            ...(typeof payload.useDataProfile === 'boolean' ? { useDataProfile: payload.useDataProfile } : {}),
          },
        }).unwrap();

        currentReportIdRef.current = response.reportId;
        setReportId(response.reportId);
        setActiveTab('report');
        invalidateCredits();
      } catch (error) {
        currentReportIdRef.current = null;
        setReportErrorModal({
          reportId: null,
          retryCount: 0,
          errorKind: null,
          errorMessage: extractMarketIntelligenceErrorMessage(error) || t('generating.errors.unexpected'),
        });
        setActiveTab('setup');
      } finally {
        setIsGenerating(false);
      }
    },
    [currentLanguage, invalidateCredits, startAnalysis, t, workspace.id]
  );

  const handleReportError = useCallback(
    (failure: MarketIntelligenceReportFailure) => {
      if (failure.reportId !== currentReportIdRef.current) {
        return;
      }

      currentReportIdRef.current = null;
      setIsGenerating(false);
      setReport(null);
      setReportId(null);
      setActiveTab('setup');
      setReportErrorModal({
        reportId: failure.reportId,
        retryCount: failure.retryCount,
        errorKind: failure.errorKind,
        errorMessage: failure.errorMessage || t('generating.errors.unexpected'),
      });
      invalidateCredits();
    },
    [invalidateCredits, t]
  );

  const handleCloseReportErrorModal = useCallback(() => {
    currentReportIdRef.current = null;
    setReportErrorModal(null);
    setActiveTab('setup');
  }, []);

  const handleRetryReport = useCallback(async () => {
    if (!reportErrorModal?.reportId) {
      return;
    }

    try {
      const response = await retryReport({
        workspaceId: workspace.id,
        reportId: reportErrorModal.reportId,
        body: {
          language: currentLanguage,
        },
      }).unwrap();

      currentReportIdRef.current = response.reportId;
      setReportErrorModal(null);
      setReport(null);
      setReportId(response.reportId);
      setActiveTab('report');
      invalidateCredits();
    } catch (error) {
      currentReportIdRef.current = reportErrorModal.reportId;
      setReportErrorModal({
        reportId: reportErrorModal.reportId,
        retryCount: reportErrorModal.retryCount,
        errorKind: null,
        errorMessage: extractMarketIntelligenceErrorMessage(error) || t('generating.errors.unexpected'),
      });
      setActiveTab('setup');
    }
  }, [currentLanguage, invalidateCredits, reportErrorModal, retryReport, t, workspace.id]);

  const handleRegenerateReport = useCallback(async () => {
    if (!lastRequest) {
      return;
    }

    setActiveTab('report');
    await generateReport(lastRequest);
  }, [generateReport, lastRequest]);

  return {
    activeTab,
    generateReport,
    handleCloseReportErrorModal,
    handleRegenerateReport,
    handleReportError,
    handleReportReady,
    handleRetryReport,
    isGenerating,
    isReportHistoryLoading,
    isReportPending: Boolean(reportId) && !report,
    isRetryingReport,
    latestReportGenerationFailed,
    maxReportRetryCount: MAX_REPORT_RETRY_COUNT,
    report,
    reportErrorModal,
    reportId,
    setActiveTab,
  };
};
